/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : detail component
 *   Integrations : Backend API integrated
 *   Version : 1.0.2
 */

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { useParams, useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../Components/Buttons";
import { Input, CustomFileUpload } from "../../Components/FormElements";
import { AboutSections, Funds } from "./Helper";
import { APIService } from "../../Service";
import { EmptyScreen, Loader } from "../../Components/Other";
import { toast } from "react-toastify";

export default function BasketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loader, setLoader] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Form states
  const [basketName, setBasketName] = useState("");
  const [basketNameError, setBasketNameError] = useState("");

  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");

  // Best For (numbers only — year count)
  const [bestFor, setBestFor] = useState("");
  const [bestForError, setBestForError] = useState("");

  // Risk (text only)
  const [risk, setRisk] = useState("");
  const [riskError, setRiskError] = useState("");

  const [minSip, setMinSip] = useState("");
  const [minSipError, setMinSipError] = useState("");

  const [minLumpsum, setMinLumpsum] = useState("");
  const [minLumpsumError, setMinLumpsumError] = useState("");

  // About sections managed via AboutSections component
  const [aboutItems, setAboutItems] = useState([]);
  const [aboutErrorState, setAboutErrorState] = useState({
    general: "",
    items: [],
  });

  // Funds data from Funds component
  const [fundGroups, setFundGroups] = useState([]);
  const [fundsErrorState, setFundsErrorState] = useState({
    general: "",
    items: [],
  });

  // Backend data states
  const [basketData, setBasketData] = useState(null);
  const [createdOn, setCreatedOn] = useState("");
  const [customersInvested, setCustomersInvested] = useState("0");
  const [returnPercentage, setReturnPercentage] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [returnYear, setReturnYear] = useState("");

  useEffect(() => {
    _getBasketDetail();
  }, [id]);

  // Effects
  useEffect(() => {
    if (basketData) {
      dispatch(
        setPageTitle(["Manage Basket", basketData.name || "Basket Detail"])
      );
    }
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-basket"]));
  }, [id, basketData, dispatch]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  // Service to get basket detail
  const _getBasketDetail = () => {
    setLoader(true);
    const payload = {
      basket_style_id: parseInt(id),
    };

    APIService(true, "manage-basket/view-detail", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const data = res.data;
          setBasketData(data);

          // Set form values from API response
          setBasketName(data.name || "");
          setMinSip(data.minimum_sip?.toString() || "");
          setMinLumpsum(data.minimum_lumpsum?.toString() || "");
          setLogoUrl(data.image || "");

          // Pre-populate bestFor: API returns "5 year" — extract just the number
          if (data.best_for) {
            const yearNum = data.best_for.toString().replace(/[^0-9]/g, "");
            setBestFor(yearNum);
          } else {
            setBestFor("");
          }

          // Pre-populate risk: plain text value
          setRisk(data.risk || "");

          // Set display values
          setCreatedOn(data.created_on || "");
          setLastUpdated(data.updated_on || "");
          setCustomersInvested(data.invested_users_count || "0");
          setReturnPercentage(data.return_percentage || "");
          setReturnYear(data.return_year || "3Y");

          // Set about sections
          if (data.about_basket && data.about_basket.length > 0) {
            const aboutSections = data.about_basket.map((item, idx) => ({
              id: `about_${idx}`,
              text: item.title || "",
              file: null,
              fileUrl: item.image || "",
            }));
            setAboutItems(aboutSections);
          } else {
            // Initialize with one empty section
            setAboutItems([
              { id: "about_default", text: "", file: null, fileUrl: "" },
            ]);
          }

          // Set fund groups
          if (data.fund_list && data.fund_list.funds) {
            const funds = data.fund_list.funds.map((fund) => ({
              selectedFund: {
                value: fund.fund_id,
                label: fund.fund_name,
                image: fund.fund_image,
              },
              fundType: fund.fund_type,
              allocation: fund.allocation_percentage?.toString() || "",
              allocationId: fund.allocation_id,
            }));
            setFundGroups(funds);
          }

          setIsEmpty(false);
        } else {
          setIsEmpty(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching basket details:", error);
        toast.error("Failed to load basket details");
        setIsEmpty(true);
      })
      .finally(() => setLoader(false));
  };

  // Helper function to clear all errors
  const clearAllErrors = () => {
    setBasketNameError("");
    setLogoError("");
    setBestForError("");
    setRiskError("");
    setMinSipError("");
    setMinLumpsumError("");
    setAboutErrorState({ general: "", items: [] });
    setFundsErrorState({ general: "", items: [] });
  };

  // Handle cancel button
  const handleCancel = () => {
    clearAllErrors();
    navigate("/manage-basket");
  };

  // Child callbacks
  const handleAboutItemsChange = (updatedItems) => {
    setAboutItems(updatedItems);
  };

  const handleFundsSubmit = (groups) => {
    setFundGroups(groups);
  };

  const handleLogoUploadComplete = (url) => {
    setLogoUrl(url);
    setLogoFile(null);
    setLogoError("");
  };

  // Form validation
  const validateForm = () => {
    let valid = true;

    // Reset all errors
    setBasketNameError("");
    setLogoError("");
    setBestForError("");
    setRiskError("");
    setMinSipError("");
    setMinLumpsumError("");
    setAboutErrorState({ general: "", items: [] });
    setFundsErrorState({ general: "", items: [] });

    // Basket name validation
    if (!basketName.trim()) {
      setBasketNameError("Basket name is required");
      valid = false;
    } else if (basketName.trim().length < 3) {
      setBasketNameError("Basket name must be at least 3 characters");
      valid = false;
    }

    // Logo validation
    if (!logoFile && !logoUrl) {
      setLogoError("Please upload a basket logo");
      valid = false;
    }

    // Best For validation — positive integer only
    if (!bestFor.toString().trim()) {
      setBestForError("Best For is required");
      valid = false;
    } else if (isNaN(bestFor) || parseInt(bestFor) <= 0) {
      setBestForError("Best For must be a valid positive number");
      valid = false;
    } else if (!Number.isInteger(Number(bestFor))) {
      setBestForError("Best For must be a whole number (e.g. 5)");
      valid = false;
    }

    // Risk validation — text only, no digits
    if (!risk.trim()) {
      setRiskError("Risk is required");
      valid = false;
    } else if (/\d/.test(risk.trim())) {
      setRiskError("Risk must contain only text (e.g. Low, Medium, High)");
      valid = false;
    } else if (risk.trim().length < 2) {
      setRiskError("Risk must be at least 2 characters");
      valid = false;
    }

    // About section validation
    if (!Array.isArray(aboutItems) || aboutItems.length === 0) {
      setAboutErrorState({
        general: "Add at least one about section",
        items: [
          {
            textError: "About description is required",
            fileError: "Please upload an image",
          },
        ],
      });
      valid = false;
    } else {
      const aboutErrors = aboutItems.map(() => ({
        textError: "",
        fileError: "",
      }));

      aboutItems.forEach((item, idx) => {
        if (!item.text?.trim()) {
          aboutErrors[idx].textError = "About description is required";
          valid = false;
        }
        if (!item.file && !item.fileUrl) {
          aboutErrors[idx].fileError = "Please upload an image";
          valid = false;
        }
      });

      if (aboutErrors.some((e) => e.textError || e.fileError)) {
        setAboutErrorState({
          general: "Please fix the highlighted about section errors",
          items: aboutErrors,
        });
      }
    }

    // Min SIP validation
    if (!minSip.toString().trim()) {
      setMinSipError("Min SIP is required");
      valid = false;
    } else if (isNaN(minSip) || parseInt(minSip) <= 0) {
      setMinSipError("Min SIP must be a valid positive number");
      valid = false;
    } else if (parseInt(minSip) < 100) {
      setMinSipError("Min SIP must be at least ₹100");
      valid = false;
    }

    // Min Lumpsum validation
    if (!minLumpsum.toString().trim()) {
      setMinLumpsumError("Min Lumpsum is required");
      valid = false;
    } else if (isNaN(minLumpsum) || parseInt(minLumpsum) <= 0) {
      setMinLumpsumError("Min Lumpsum must be a valid positive number");
      valid = false;
    } else if (parseInt(minLumpsum) < 500) {
      setMinLumpsumError("Min Lumpsum must be at least ₹500");
      valid = false;
    } else if (parseInt(minLumpsum) < parseInt(minSip)) {
      setMinLumpsumError(
        "Min Lumpsum should be greater than or equal to Min SIP"
      );
      valid = false;
    }

    // Funds section validation
    if (fundGroups.length === 0) {
      setFundsErrorState({
        general: "Add at least one fund allocation group",
        items: [
          {
            fundError: "Select a fund",
            fundTypeError: "Select fund type",
            allocationError: "Enter valid allocation",
          },
        ],
      });
      valid = false;
      return valid;
    }

    // Total allocation must be exactly 100
    const totalAllocation = fundGroups.reduce(
      (sum, g) => sum + Number(g.allocation || 0),
      0
    );

    if (totalAllocation !== 100) {
      setFundsErrorState({
        general: "Total allocation must be exactly 100%",
        items: fundGroups.map(() => ({
          fundError: "",
          fundTypeError: "",
          allocationError: "",
        })),
      });
      valid = false;
    }

    // Per-row validation
    const fundItemErrors = fundGroups.map((g) => ({
      fundError: !g.selectedFund ? "Select a fund" : "",
      fundTypeError: !g.fundType ? "Select fund type" : "",
      allocationError:
        !g.allocation || Number(g.allocation) <= 0
          ? "Enter valid allocation"
          : "",
    }));

    const hasFundErrors = fundItemErrors.some(
      (e) => e.fundError || e.fundTypeError || e.allocationError
    );

    if (hasFundErrors) {
      setFundsErrorState({
        general: "All fund groups must be completely filled",
        items: fundItemErrors,
      });
      valid = false;
    }

    // Clear fund errors if all valid
    if (totalAllocation === 100 && !hasFundErrors) {
      setFundsErrorState({
        general: "",
        items: fundGroups.map(() => ({
          fundError: "",
          fundTypeError: "",
          allocationError: "",
        })),
      });
    }

    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);

    // Prepare payload — best_for sent as "<number> year" to match API spec
    const payload = {
      basket_style_id: parseInt(id),
      name: basketName.trim(),
      image: logoUrl,
      best_for: `${parseInt(bestFor)} year`,
      risk: risk.trim(),
      about_sections: aboutItems.map((item) => ({
        title: item.text,
        image: item.fileUrl || "",
      })),
      min_sip: parseInt(minSip),
      min_lumpsum: parseInt(minLumpsum),
      funds: fundGroups.map((fund) => ({
        fund_id: fund.selectedFund?.value || fund.selectedFund?.fund_id,
        fund_type: `${fund.fundType?.value}` || `${fund.fundType}`,
        allocation_percentage: parseFloat(fund.allocation),
      })),
    };

    console.log(payload);

    APIService(true, "manage-basket/upsert", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message || "Basket updated successfully");
          navigate("/manage-basket");
        } else {
          toast.error(res.message || "Failed to update basket");
          console.error("Error updating basket:", res.message);
        }
      })
      .catch((error) => {
        toast.error(error.message || "Failed to update basket");
        console.error("Error updating basket:", error);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  return loader ? (
    <Loader />
  ) : isEmpty ? (
    <EmptyScreen message={"Basket detail not found"} />
  ) : (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card bg-transparent rounded-24 p-24 d-flex flex-column row-gap-3">
          {/* TOP CARD */}
          <div className="row">
            <div className="col-12">
              <div className="d-flex w-100 flex-column e-glass-card bg-opacity-43 shadow-none rounded-16 rg-24 p-24">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center text-eerie-black">
                    <div className="e-w-fit rounded-8 bg-white e-border border-facebook-blue-4 border-opacity-10 p-1">
                      <img
                        src={
                          logoUrl || require("../../Assets/Images/avatar.jpg")
                        }
                        alt="basket image"
                        width={58}
                      />
                    </div>
                    <h5 className="fs-18 ps-19 lh-20 fw-500 mb-0 text-capitalize">
                      {basketName}
                    </h5>
                  </div>

                  <div className="d-flex align-items-center justify-content-end">
                    <PrimaryButton
                      type={2}
                      icon="chevron-right"
                      ClassName="bg-transparent p-0 fs-14 fw-400 lh-20 text-facebook-blue"
                      size="12px"
                      onPress={() => {
                        navigate(
                          `/manage-basket/detail/${id}/rebalance-history`
                        );
                      }}
                      name={"View Rebalance History"}
                    />
                  </div>
                </div>

                {/* STATS */}
                <div className="row row-cols-lg-5 row-cols-md-4 row-cols-sm-3 row-cols-2 rg-32">
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">Created On</h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0">
                      {createdOn}
                    </p>
                  </div>

                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      Customers Invested
                    </h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0 text-truncate">
                      {customersInvested}
                    </p>
                  </div>

                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">Min SIP</h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0 text-truncate">
                      ₹{minSip}
                    </p>
                  </div>

                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      Min Lumpsum
                    </h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0 text-truncate">
                      ₹{minLumpsum}
                    </p>
                  </div>

                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      {returnYear} Returns
                    </h4>
                    <p className="fw-400 fs-18 text-medium-sea-green m-0 text-truncate">
                      {returnPercentage}%
                    </p>
                  </div>

                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      Last Updated
                    </h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0">
                      {lastUpdated}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* UPDATE FORM */}
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-column e-glass-card bg-opacity-43 shadow-none rounded-16 row-gap-3 p-24">
                <div className="row">
                  <div className="col-lg-6 col-12">
                    <h4 className="fw-500 fs-18 text-eerie-black">
                      Update details
                    </h4>

                    {/* Basket Name */}
                    <Input
                      id="basket_name"
                      type="text"
                      label="Basket Name"
                      placeholder="Enter Basket Name"
                      className="w-100 px-3 py-10"
                      require={true}
                      value={basketName}
                      error={basketNameError}
                      handleChange={(v) => {
                        setBasketName(v);
                        setBasketNameError("");
                      }}
                    />

                    {/* Basket Logo */}
                    <div className="d-flex flex-column row-gap-2 pb-2">
                      <p className="mb-0 fw-400 fs-14">
                        Upload logo <span className="text-cg-red">*</span>
                      </p>
                      <CustomFileUpload
                        file={logoFile || logoUrl}
                        type={2}
                        fileSize={3}
                        handleClear={() => {
                          setLogoFile(null);
                          setLogoUrl("");
                        }}
                        error={logoError}
                        handleChange={(file) => {
                          setLogoFile(file);
                          setLogoError("");
                        }}
                        onUploadComplete={handleLogoUploadComplete}
                      />
                    </div>

                    {/* Best For — numbers only, "Years" suffix label on right */}
                    <div className="d-flex w-100 position-relative">
                      <Input
                        id="best_for"
                        type="number"
                        label="Best For"
                        placeholder="Enter number of years"
                        className="w-100 px-3 py-10 pe-5"
                        require={true}
                        value={bestFor}
                        error={bestForError}
                        handleChange={(v) => {
                          // Allow only positive integers
                          const numValue = v.replace(/[^0-9]/g, "");
                          setBestFor(numValue);
                          setBestForError("");
                        }}
                      />
                      <div className="position-absolute end-0 pt-4 mt-3 pe-3 w-max h-100">
                        <p className="mb-0 e-primary-font-rubik fs-14 bg-white ps-2 fw-400">
                          Years
                        </p>
                      </div>
                    </div>

                    {/* Risk — text only, digits stripped on input */}
                    <Input
                      id="risk"
                      type="text"
                      label="Risk"
                      placeholder="Enter Risk (e.g. Low, Medium, High)"
                      className="w-100 px-3 py-10"
                      require={true}
                      value={risk}
                      error={riskError}
                      handleChange={(v) => {
                        // Strip any digits the user may try to type
                        const textOnly = v.replace(/[0-9]/g, "");
                        setRisk(textOnly);
                        setRiskError("");
                      }}
                    />

                    {/* About Section */}
                    <AboutSections
                      data={aboutItems}
                      onChange={handleAboutItemsChange}
                      maxItems={5}
                      errorState={aboutErrorState}
                      setErrorState={setAboutErrorState}
                    />

                    <h4 className="fw-500 fs-18 text-eerie-black pt-24">
                      Min SIP & Lumpsum
                    </h4>

                    <div className="position-relative">
                      <Input
                        id="min_sip"
                        type="Number"
                        label="Min SIP"
                        placeholder="Enter Min SIP"
                        require={true}
                        value={minSip}
                        error={minSipError}
                        className="w-100 ps-4 pe-3 py-10"
                        handleChange={(v) => {
                          const numValue = v.replace(/[^0-9]/g, "");
                          setMinSip(numValue);
                          setMinSipError("");
                        }}
                      />
                      <span
                        style={{ top: "41%", left: "2%" }}
                        className="position-absolute"
                      >
                        ₹
                      </span>
                    </div>

                    <div className="position-relative">
                      <Input
                        id="min_lumpsum"
                        type="Number"
                        label="Min Lumpsum"
                        placeholder="Enter Min Lumpsum"
                        require={true}
                        className="w-100 ps-4 pe-3 py-10"
                        value={minLumpsum}
                        error={minLumpsumError}
                        handleChange={(v) => {
                          const numValue = v.replace(/[^0-9]/g, "");
                          setMinLumpsum(numValue);
                          setMinLumpsumError("");
                        }}
                      />
                      <span
                        style={{ top: "41%", left: "2%" }}
                        className="position-absolute"
                      >
                        ₹
                      </span>
                    </div>
                  </div>

                  <div className="col-lg-10 col-12">
                    {/* Funds management */}
                    <Funds
                      data={fundGroups}
                      onSubmit={handleFundsSubmit}
                      errorState={fundsErrorState}
                      setErrorState={setFundsErrorState}
                    />

                    <div className="row">
                      <div className="offset-lg-6 col-lg-6 col-12">
                        <div className="row row-cols-2 pt-24">
                          <div className="col">
                            <PrimaryButton
                              name="Cancel"
                              ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                              onPress={handleCancel}
                            />
                          </div>
                          <div className="col">
                            <PrimaryButton
                              name={submitLoading ? "Processing..." : "Update"}
                              ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                              onPress={handleSubmit}
                              disabled={submitLoading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}